package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CharacterSheet
import fr.manyverse.app.domain.model.CharacterToCreate
import fr.manyverse.app.domain.model.CharacterToUpdate
import fr.manyverse.app.domain.repository.CharacterCacheRepository
import fr.manyverse.app.domain.repository.CharacterRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

private const val TAG = "CachingCharacterRepository"

class CachingCharacterRepository(
    private val remoteRepository: CharacterRepository,
    private val cacheRepository: CharacterCacheRepository,
    private val backgroundScope: CoroutineScope
) : CharacterRepository {

    private var cachedUniverseId: String? = null
    private var cachedUserId: String? = null

    override suspend fun getCharactersByUserId(
        universeId: String,
        userId: String
    ): Result<List<Character>> {
        val contextChanged = cachedUniverseId != null && (universeId != cachedUniverseId || userId != cachedUserId)
        if (contextChanged) {
            cacheRepository.clear()
        }
        cachedUniverseId = universeId
        cachedUserId = userId

        val cachedCharacters = cacheRepository.getCharacters()
        if (cachedCharacters != null) {
            refreshCacheInBackground(universeId, userId)
            return Result.success(cachedCharacters.sortedByDescending { it.createdAt })
        }

        return remoteRepository.getCharactersByUserId(universeId, userId).also { result ->
            result.onSuccess { characters ->
                cacheRepository.setCharacters(characters)
            }
        }
    }

    override suspend fun getAllCharactersByUserId(
        userId: String
    ): Result<List<Character>> {
        return remoteRepository.getAllCharactersByUserId(userId)
    }

    override suspend fun countCharactersByUserId(userId: String): Result<Int> {
        return remoteRepository.countCharactersByUserId(userId)
    }

    override suspend fun countCharactersInUniverse(
        universeId: String,
        userId: String
    ): Result<Int> {
        return remoteRepository.countCharactersInUniverse(universeId, userId)
    }

    override suspend fun createCharacter(
        universeId: String,
        character: CharacterToCreate
    ): Result<Character> {
        return remoteRepository.createCharacter(universeId, character).also { result ->
            result.onSuccess { newCharacter ->
                cacheRepository.upsertCharacter(newCharacter)
            }
        }
    }

    override suspend fun updateCharacter(
        characterId: String,
        character: CharacterToUpdate
    ): Result<Character> {
        return remoteRepository.updateCharacter(characterId, character)
            .also { result ->
                result.onSuccess { updatedCharacter ->
                    cacheRepository.upsertCharacter(updatedCharacter)
                }
            }
    }

    override suspend fun updateCharacterSheet(
        characterId: String,
        sheet: CharacterSheet
    ): Result<Character> {
        return remoteRepository.updateCharacterSheet(characterId, sheet)
            .also { result ->
                result.onSuccess { updatedCharacter ->
                    cacheRepository.upsertCharacter(updatedCharacter)
                }
            }
    }

    override suspend fun deleteCharacter(
        characterId: String
    ): Result<Unit> {
        return remoteRepository.deleteCharacter(characterId).also { result ->
            result.onSuccess {
                cacheRepository.removeCharacter(characterId)
            }
        }
    }

    override suspend fun getCharacterById(
        characterId: String
    ): Result<Character?> {
        return remoteRepository.getCharacterById(characterId)
    }

    fun getCachedCharacters(): List<Character>? {
        return cacheRepository.getCharacters()
    }

    fun isCacheLoaded(): Boolean {
        return cacheRepository.getCharacters() != null
    }

    fun clearCache() {
        cacheRepository.clear()
        cachedUniverseId = null
        cachedUserId = null
    }

    private fun refreshCacheInBackground(universeId: String, userId: String) {
        backgroundScope.launch {
            remoteRepository.getCharactersByUserId(universeId, userId)
                .onSuccess { characters ->
                    cacheRepository.setCharacters(characters)
                }
                .onFailure {
                    AppLogger.warn(TAG, "Background cache refresh failed for user=$userId")
                }
        }
    }
}
